import axios, { type AxiosResponse } from 'axios'
import { notify } from './Notifications'
import { nextTick } from 'vue'

export const api = new class {
  bearerToken: string = ''

  axios = axios.create({
    withCredentials: true,
    headers: {
    },
  })

  getBackendUrl() {
    let r = (import.meta.server)
      ? useRuntimeConfig().public.SERVER_URL_INTERNAL
      : useRuntimeConfig().public.SERVER_URL

    if (import.meta.server){
      this.axios.defaults.headers.common['X-Forwarded-For'] = useRequestHeaders()?.['x-real-ip'] ?? useRequestHeaders()?.['x-forwarded-for']
      this.axios.defaults.headers.common['X-Real-Ip'] = useRequestHeaders()?.['x-real-ip'] ?? useRequestHeaders()?.['x-forwarded-for']
    }

    if (!r) {
      throw new Error('SERVER_URL not defined in runtime config')
    }

    // if last is not /
    if (r[r.length - 1] !== '/') {
      r += '/'
    }

    return r
  }

  clientAsset(str: string) {
    return this.getBackendUrl() + 'warehouse/' + str +'?clientasset=true'
  }

  cleanseUrl(url: string) {
    if (url[0] === '/') {
      url = url.substring(1)

    }else{
      // if first 3 are not api
      if (url.substring(0, 3) !== 'api') {
        url = 'api/' + url
      }
    }


    return url
  }

  setBearer(token: string|null) {

    if(!token){
      this.bearerToken = ''
      localStorage.removeItem('bearerToken')
      sessionStorage.removeItem('bearerToken')
      delete this.axios.defaults.headers.common['Authorization']
      return
    }else{
      this.bearerToken = token
      localStorage.setItem('bearerToken', token)

      // set default header for axios
      this.axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
    }
  }

  postware(response: any) {
    return response
      .then((r: AxiosResponse) => {
        return r
      })
      .catch((e: any) => {
        throw e;
      })
  }

  post<T>(url: string, data: any = undefined, headers: any = undefined) : Promise<AxiosResponse<T>> {
    return this.postware(this.axios.post(this.getBackendUrl() + this.cleanseUrl(url), data, {
      headers: {
        'Authorization': 'Bearer ' + this.bearerToken,
        'Content-Type': headers?.['Content-Type'] ?? 'application/json',
        'Accept': 'application/json',
      },
    }))
  }

  // new helper to recursively serialize nested objects into query parameters
  private serializeParams(params: any, prefix = ''): string {
    const queryParts: string[] = []
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key]
        const newKey = prefix ? `${prefix}[${encodeURIComponent(key)}]` : encodeURIComponent(key)
        if (value !== null && typeof value === 'object') {
          queryParts.push(this.serializeParams(value, newKey))
        } else {
          queryParts.push(`${newKey}=${encodeURIComponent(value)}`)
        }
      }
    }
    return queryParts.join('&')
  }

  get<T>(url: string, data: any = undefined) : Promise<AxiosResponse<T>> {
    // Replace URLSearchParams with serializeParams for handling nested objects
    let urlParams = data ? this.serializeParams(data) : ''

    if (urlParams.length > 0) {
      urlParams = '?' + urlParams
    }

    return this.postware(this.axios.get(this.getBackendUrl() + this.cleanseUrl(url) + urlParams, {
      headers: {
        'Authorization': 'Bearer ' + this.bearerToken,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }))
  }

  put<T>(url: string, data: any = undefined) : Promise<AxiosResponse<T>> {
    return this.postware(this.axios.put(this.getBackendUrl() + this.cleanseUrl(url), data, {
      headers: {
        'Authorization': 'Bearer ' + this.bearerToken,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }))
  }

  delete<T>(url: string, headers: any = undefined) : Promise<AxiosResponse<T>> {
    return this.postware(this.axios.delete(this.getBackendUrl() + this.cleanseUrl(url), {
      headers: {
        'Authorization': 'Bearer ' + this.bearerToken,
        'Content-Type': headers?.['Content-Type'] ?? 'application/json',
        'Accept': 'application/json',
      },
    }))
  }

  async xsrf() {
    console.log('Getting xsrf')
    return this.get('/sanctum/csrf-cookie')
  }

  parseError(e: any) {
    if(!(e?.response?.data?.message ?? e?.response?.message ?? e?.message)) throw e

    notify({
      text: e?.response?.data?.message ?? e?.response?.message ?? e?.message ?? 'An unknown error occurred',
      type: 'error',
      errors: e?.response?.data?.errors,
      timeout: 3000,
    })

    throw e
  }

  parseThen = new Proxy(
    (res: unknown, fallback: unknown): void => {
      nextTick(() => {
        const r = res as any
        const msg = r?.data?.message ?? r?.message ?? fallback ?? "Success"
        ;(notify as (m: any) => void)(msg as any)
      })
    },
    {
      get: (target, prop) => {
        if (prop === 'default') {
          return (msg: string) => (res: unknown) => {
            target(res, msg)
          }
        }
        return Reflect.get(target, prop)
      },
      apply: (target, thisArg, argArray) => {
        if (!argArray.length || !argArray[0]?.message) {
          let target_called = false

          const handler = ((res: unknown) => target(res, null)) as ((res: unknown) => void) & {
            default: (msg: string) => (res: unknown) => void
          }
          handler.default = (msg: string) => (res: unknown) => {
            target_called = true
            target(res, msg)
          }

          if(argArray[0] && !argArray[0]?.message){
            setTimeout(() => {
              if (!target_called) {
                const [res] = argArray
                if (res && typeof res === 'object' && ('data' in (res as any) || 'message' in (res as any))) {
                  target(res, null)
                  return
                }
                return Reflect.apply(target, thisArg, argArray)
              }
            }, 20)
          }

          return handler
        }

        const [res] = argArray

        if (res && typeof res === 'object' && ('data' in (res as any) || 'message' in (res as any))) {
          target(res, null)
          return
        }

        return Reflect.apply(target, thisArg, argArray)
      }
    }
  ) as {
    (res: unknown): void;
    (): ((res: unknown) => void) & { default: (msg: string) => (res: unknown) => void };
    default: (msg: string) => (res: unknown) => void;
  }

}
